import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Save, Loader2, Globe, Palette, Phone, Share2, FileText, Settings as SettingsIcon } from 'lucide-react';

const Settings = () => {
  const { token } = useAdmin();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '',
    site_tagline: '',
    logo_url: '',
    primary_color: '#B8860B',
    secondary_color: '#D4A84B',
    background_color: '#1a1a1a',
    text_color: '#E8DDCB',
    heading_font: 'Playfair Display',
    body_font: 'Inter',
    phone: '',
    whatsapp: '',
    whatsapp_message: '',
    email: '',
    address: '',
    instagram_url: '',
    facebook_url: '',
    youtube_url: '',
    tiktok_url: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    catalog_pdf_url: '',
    catalog_enabled: true,
    whatsapp_button_enabled: true,
    privacy_policy: '',
    terms_of_service: '',
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Configurações guardadas",
        description: "As alterações foram guardadas com sucesso."
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível guardar as configurações.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#B8860B]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Configurações</h1>
          <p className="text-white/60">Gerir as configurações gerais do site</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#B8860B] hover:bg-[#D4A84B] text-black">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
      </div>

      <Tabs defaultValue="geral" className="space-y-6">
        <TabsList className="bg-[#242424] border border-[#B8860B]/20">
          <TabsTrigger value="geral" className="data-[state=active]:bg-[#B8860B] data-[state=active]:text-black">
            <Globe className="w-4 h-4 mr-2" />Geral
          </TabsTrigger>
          <TabsTrigger value="cores" className="data-[state=active]:bg-[#B8860B] data-[state=active]:text-black">
            <Palette className="w-4 h-4 mr-2" />Cores
          </TabsTrigger>
          <TabsTrigger value="contacto" className="data-[state=active]:bg-[#B8860B] data-[state=active]:text-black">
            <Phone className="w-4 h-4 mr-2" />Contacto
          </TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-[#B8860B] data-[state=active]:text-black">
            <Share2 className="w-4 h-4 mr-2" />Redes Sociais
          </TabsTrigger>
          <TabsTrigger value="seo" className="data-[state=active]:bg-[#B8860B] data-[state=active]:text-black">
            <FileText className="w-4 h-4 mr-2" />SEO
          </TabsTrigger>
        </TabsList>

        {/* Geral */}
        <TabsContent value="geral">
          <Card className="bg-[#242424] border-[#B8860B]/20">
            <CardHeader>
              <CardTitle className="text-white">Informações Gerais</CardTitle>
              <CardDescription className="text-white/60">Nome e identidade do site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Nome do Site</Label>
                  <Input
                    value={settings.site_name}
                    onChange={(e) => handleChange('site_name', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Tagline</Label>
                  <Input
                    value={settings.site_tagline}
                    onChange={(e) => handleChange('site_tagline', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">URL do Logo</Label>
                <Input
                  value={settings.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">URL do Catálogo PDF</Label>
                <Input
                  value={settings.catalog_pdf_url}
                  onChange={(e) => handleChange('catalog_pdf_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://..."
                />
              </div>
              <div className="flex items-center justify-between pt-4">
                <div>
                  <Label className="text-white">Botão WhatsApp</Label>
                  <p className="text-white/60 text-sm">Mostrar botão flutuante de WhatsApp</p>
                </div>
                <Switch
                  checked={settings.whatsapp_button_enabled}
                  onCheckedChange={(checked) => handleChange('whatsapp_button_enabled', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Página de Catálogo</Label>
                  <p className="text-white/60 text-sm">Ativar página de catálogo</p>
                </div>
                <Switch
                  checked={settings.catalog_enabled}
                  onCheckedChange={(checked) => handleChange('catalog_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cores */}
        <TabsContent value="cores">
          <Card className="bg-[#242424] border-[#B8860B]/20">
            <CardHeader>
              <CardTitle className="text-white">Paleta de Cores</CardTitle>
              <CardDescription className="text-white/60">Personalize as cores do site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Cor Principal</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Cor Secundária</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Cor de Fundo</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.background_color}
                      onChange={(e) => handleChange('background_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.background_color}
                      onChange={(e) => handleChange('background_color', e.target.value)}
                      className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Cor do Texto</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.text_color}
                      onChange={(e) => handleChange('text_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.text_color}
                      onChange={(e) => handleChange('text_color', e.target.value)}
                      className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-white">Fonte dos Títulos</Label>
                  <Input
                    value={settings.heading_font}
                    onChange={(e) => handleChange('heading_font', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Fonte do Corpo</Label>
                  <Input
                    value={settings.body_font}
                    onChange={(e) => handleChange('body_font', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacto */}
        <TabsContent value="contacto">
          <Card className="bg-[#242424] border-[#B8860B]/20">
            <CardHeader>
              <CardTitle className="text-white">Informações de Contacto</CardTitle>
              <CardDescription className="text-white/60">Telefone, email e morada</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Telefone</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">WhatsApp (só números)</Label>
                  <Input
                    value={settings.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                    placeholder="+351918288122"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Mensagem Pré-definida WhatsApp</Label>
                <Input
                  value={settings.whatsapp_message}
                  onChange={(e) => handleChange('whatsapp_message', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Email</Label>
                <Input
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Morada</Label>
                <Textarea
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social */}
        <TabsContent value="social">
          <Card className="bg-[#242424] border-[#B8860B]/20">
            <CardHeader>
              <CardTitle className="text-white">Redes Sociais</CardTitle>
              <CardDescription className="text-white/60">Links para as redes sociais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Instagram</Label>
                <Input
                  value={settings.instagram_url}
                  onChange={(e) => handleChange('instagram_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Facebook</Label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => handleChange('facebook_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">YouTube</Label>
                <Input
                  value={settings.youtube_url}
                  onChange={(e) => handleChange('youtube_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">TikTok</Label>
                <Input
                  value={settings.tiktok_url}
                  onChange={(e) => handleChange('tiktok_url', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="https://tiktok.com/..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo">
          <Card className="bg-[#242424] border-[#B8860B]/20">
            <CardHeader>
              <CardTitle className="text-white">SEO</CardTitle>
              <CardDescription className="text-white/60">Otimização para motores de busca</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Meta Título</Label>
                <Input
                  value={settings.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Meta Descrição</Label>
                <Textarea
                  value={settings.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Palavras-chave</Label>
                <Input
                  value={settings.meta_keywords}
                  onChange={(e) => handleChange('meta_keywords', e.target.value)}
                  className="bg-[#1a1a1a] border-[#B8860B]/30 text-white"
                  placeholder="carne, bbq, churrasco, ..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
